import React, { useState, useEffect } from 'react';
import { userHasRole } from '../../utils/roleHelpers';
import { 
  Clock, Search, Edit2, Trash2, Play, X, Loader2, 
  AlertCircle, Calendar, Users, MessageSquare, Pause,
  RefreshCw
} from 'lucide-react';
import { whatsappAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SchedulerHealthStrip from './SchedulerHealthStrip';

/**
 * WhatsApp Scheduled Messages Page
 * View and manage scheduled broadcasts
 */
const WhatsAppScheduled = () => {
  const { user } = useAuth();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, broadcast: null });
  
  const isAdmin = userHasRole(user, 'admin');
  
  useEffect(() => {
    loadScheduled();
  }, []);
  
  const loadScheduled = async () => {
    try {
      setLoading(true);
      const response = await whatsappAPI.getScheduledBroadcasts();
      setBroadcasts(response.data.data || []);
    } catch (error) {
      console.error('Error loading scheduled broadcasts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendNow = async (broadcastId) => {
    try {
      setSendingId(broadcastId);
      await whatsappAPI.sendBroadcast(broadcastId);
      loadScheduled();
    } catch (error) {
      console.error('Error sending broadcast:', error);
      alert('Failed to send broadcast');
    } finally {
      setSendingId(null);
    }
  };
  
  const handleDelete = async () => {
    if (!deleteModal.broadcast) return;
    
    try {
      await whatsappAPI.deleteBroadcast(deleteModal.broadcast.id);
      loadScheduled();
      setDeleteModal({ isOpen: false, broadcast: null });
    } catch (error) {
      console.error('Error deleting broadcast:', error);
      if (error.response?.status === 403) {
        alert('Only Admin can delete broadcasts');
      }
    }
  };
  
  const formatScheduledTime = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = date - now;
    
    // If in the past
    if (diff < 0) {
      return 'Overdue';
    }
    
    // If within 24 hours
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
      if (hours > 0) {
        return `In ${hours}h ${minutes}m`;
      }
      return `In ${minutes} minutes`;
    }
    
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  return (
    <div className="space-y-6" data-testid="whatsapp-scheduled-page">
      {isAdmin && <SchedulerHealthStrip onRunNow={loadScheduled} />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Calendar className="text-blue-600" size={24} />
            Scheduled Messages
          </h2>
          <p className="text-sm text-gray-500 mt-1">View and manage upcoming scheduled broadcasts</p>
        </div>
        <button
          onClick={loadScheduled}
          className="flex items-center px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Scheduled List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : broadcasts.length > 0 ? (
        <div className="space-y-4">
          {broadcasts.map(broadcast => (
            <div 
              key={broadcast.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
              data-testid={`scheduled-card-${broadcast.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{broadcast.name}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full font-medium">
                      Scheduled
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {broadcast.recipientCount || 0} recipients
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatScheduledTime(broadcast.scheduledAt)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {broadcast.message}
                  </p>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleSendNow(broadcast.id)}
                    disabled={sendingId === broadcast.id}
                    className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
                    title="Send Now"
                  >
                    {sendingId === broadcast.id ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Play size={14} />
                    )}
                    Send Now
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => setDeleteModal({ isOpen: true, broadcast })}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Schedule Details */}
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                <span>
                  Created: {new Date(broadcast.createdAt).toLocaleDateString('en-IN')}
                </span>
                <span>
                  Scheduled for: {new Date(broadcast.scheduledAt).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No scheduled broadcasts</h3>
          <p className="text-gray-500">
            When you schedule a broadcast for later, it will appear here
          </p>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="text-red-600" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel Scheduled Broadcast</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to cancel <span className="font-medium">"{deleteModal.broadcast?.name}"</span>?
              This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteModal({ isOpen: false, broadcast: null })}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Keep It
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Cancel Broadcast
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppScheduled;
